import { useState, useCallback } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import { useAdminStore } from '../../stores/adminStore';
import { pdfEngine } from '../../core/PDFEngine';
import { useUIStore } from '../../stores/uiStore';
import { downloadBlob } from '../../utils/fileUtils';
import './OCRPanel.css';
import '../document/Dialogs.css';

interface OCRDialogProps {
  onClose: () => void;
}

export function OCRDialog({ onClose }: OCRDialogProps) {
  const { currentPage, totalPages } = useDocumentStore();
  const { gcpApiKey } = useAdminStore();
  const [scope, setScope] = useState<'current' | 'all'>('current');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [ocrResultText, setOcrResultText] = useState<string | null>(null);

  const handleRunOCR = useCallback(async () => {
    if (!gcpApiKey) {
      useUIStore.getState().addToast({ type: 'error', message: 'Google Cloud API Key is missing. Please configure it in Admin Settings.' });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setOcrResultText(null);

    try {
      const pageNumbers = scope === 'current'
        ? [currentPage]
        : Array.from({ length: totalPages }, (_, i) => i + 1);

      let fullExtractedText = '';

      for (let i = 0; i < pageNumbers.length; i++) {
        const pageNum = pageNumbers[i];
        setStatusText(`Rendering page ${pageNum}...`);
        
        // Render to high-quality image
        const canvas = document.createElement('canvas');
        await pdfEngine.renderPage(pageNum, { scale: 2, rotation: 0, canvas });
        
        const base64Data = canvas.toDataURL('image/jpeg', 0.9).split(',')[1];

        setStatusText(`Calling Cloud Vision for page ${pageNum} (${i + 1}/${pageNumbers.length})...`);
        
        // Call Google Cloud Vision directly (Dev Mode)
        const response = await fetch(`https://vision.googleapis.com/v1/images:annotate?key=${gcpApiKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requests: [{
              image: { content: base64Data },
              features: [{ type: "DOCUMENT_TEXT_DETECTION" }]
            }]
          })
        });

        if (!response.ok) {
          throw new Error(`Vision API failed: ${response.statusText}`);
        }

        const data = await response.json();
        const text = data.responses[0]?.fullTextAnnotation?.text || '';
        
        fullExtractedText += `--- Page ${pageNum} ---\n` + text + '\n\n';
        setProgress((i + 1) / pageNumbers.length);
      }

      setOcrResultText(fullExtractedText);
      useUIStore.getState().addToast({ type: 'success', message: 'OCR completed successfully!' });
    } catch (err: any) {
      console.error('OCR failed:', err);
      useUIStore.getState().addToast({ type: 'error', message: err.message || 'OCR recognition failed' });
    } finally {
      setIsProcessing(false);
    }
  }, [gcpApiKey, scope, currentPage, totalPages]);

  const handleDownload = useCallback(() => {
    if (!ocrResultText) return;
    const blob = new Blob([ocrResultText], { type: 'text/plain;charset=utf-8' });
    downloadBlob(blob, `ocr_result_page_${scope === 'current' ? currentPage : 'all'}.txt`);
  }, [ocrResultText, scope, currentPage]);

  return (
    <div className="ocr-panel-overlay">
      <div className="ocr-panel">
        <div className="ocr-panel-header">
          <h3>🔍 Extract Text (Cloud OCR)</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>×</button>
        </div>

        <div className="ocr-panel-body">
          {!ocrResultText ? (
            <>
              <p className="dialog-description">
                Extract highly accurate text using Google Cloud Vision Document AI.
              </p>

              <div className="input-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label>Target Pages</label>
                <select className="input" value={scope} onChange={(e) => setScope(e.target.value as 'current' | 'all')}>
                  <option value="current">Current Page ({currentPage})</option>
                  <option value="all">All Pages ({totalPages} pages)</option>
                </select>
              </div>

              {!gcpApiKey && (
                <div style={{ padding: '1rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '6px', fontSize: '13px' }}>
                  <strong>Missing API Key:</strong> You need to set the Google Cloud API Key in Admin Settings first.
                </div>
              )}

              {isProcessing && (
                <div style={{ marginTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                    <span>{statusText}</span>
                    <span>{Math.round(progress * 100)}%</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-bar-fill" style={{ width: `${progress * 100}%` }} />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                <label style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)' }}>Recognized Text</label>
                <button className="btn btn-ghost btn-sm" onClick={() => navigator.clipboard.writeText(ocrResultText)}>
                  📋 Copy All
                </button>
              </div>
              <textarea
                className="input"
                style={{ flex: 1, minHeight: '300px', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', whiteSpace: 'pre-wrap', resize: 'none' }}
                value={ocrResultText}
                readOnly
              />
            </div>
          )}
        </div>

        <div className="ocr-panel-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {!ocrResultText ? (
            <button className="btn btn-primary" onClick={handleRunOCR} disabled={isProcessing || !gcpApiKey}>
              {isProcessing ? '⏳ Processing...' : 'Run OCR'}
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleDownload}>
              📥 Download
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
